/* Kurz-Feedback nach Beratung oder Abschluss (31.10.2026, Betreiber: „ein kurzer Button nur, nicht kompliziert … und ich kann dann
   Pia fragen, was gelaufen ist“). Sprechen oder tippen, wahlweise „Nachfassen am“ — der Server legt Notiz, Aufgabe und nächsten
   Schritt an (backend/kurzfeedback.py). Das Audio wird nie gespeichert. */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MessageSquarePlus, Mic, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { kurzfeedbackAPI } from '@/lib/api';

const MAX_SEKUNDEN = 120;

function berlinHeute() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());   // JJJJ-MM-TT
}

function plusTage(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/* Morgen · Montag (der nächste, nie heute) · in 1 Woche */
export function nachfassTage(heute = berlinHeute()) {
  const wt = new Date(`${heute}T12:00:00Z`).getUTCDay();          // 0 = Sonntag
  const bisMontag = ((8 - wt) % 7) || 7;
  return [['morgen', 'Morgen', plusTage(heute, 1)], ['montag', 'Montag', plusTage(heute, bisMontag)], ['woche', 'In 1 Woche', plusTage(heute, 7)]];
}

const tagDe = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.` : '');

export default function KurzFeedback({ leadId, name, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState('');
  const [tag, setTag] = useState('');
  const [eigenerTag, setEigenerTag] = useState('');
  const [sendet, setSendet] = useState(false);
  const [nimmtAuf, setNimmtAuf] = useState(false);
  const [sek, setSek] = useState(0);
  const recorder = useRef(null);
  const teile = useRef([]);
  const timer = useRef(null);
  const heute = berlinHeute();
  const vorschlaege = nachfassTage(heute);
  const nachfassen = tag === 'eigen' ? eigenerTag : (vorschlaege.find(([k]) => k === tag) || [])[2] || '';

  const stopp = () => { try { if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop(); } catch { /* egal */ } };
  useEffect(() => () => { clearInterval(timer.current); stopp(); }, []);
  const zu = () => { stopp(); setOffen(false); setText(''); setTag(''); setEigenerTag(''); };

  const senden = async ({ audio }) => {
    if (!audio && text.trim().length < 3) { toast.error('Bitte kurz sagen oder tippen, wie es gelaufen ist'); return; }
    setSendet(true);
    try {
      const r = await kurzfeedbackAPI.senden(leadId, { audio, text: audio ? undefined : text.trim(), nachfassen: nachfassen || undefined });
      toast.success(r.data.nachfassen_am ? `Gespeichert. Nachfassen am ${tagDe(r.data.nachfassen_am)} steht in deinen Aufgaben.` : 'Gespeichert.');
      zu(); onFertig?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht gespeichert werden'); }
    finally { setSendet(false); }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Aufnahme hier nicht möglich, bitte tippen'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);
      teile.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) teile.current.push(e.data); };
      rec.onstop = () => {
        clearInterval(timer.current); setNimmtAuf(false);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(teile.current, { type: rec.mimeType || 'audio/webm' });
        const endung = (rec.mimeType || '').includes('mp4') ? 'm4a' : (rec.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const f = new File([blob], `feedback.${endung}`, { type: blob.type });
        if (f.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        senden({ audio: f });
      };
      recorder.current = rec; rec.start(1000);
      setNimmtAuf(true); setSek(0);
      timer.current = setInterval(() => setSek((s) => { if (s + 1 >= MAX_SEKUNDEN) stopp(); return s + 1; }), 1000);
    } catch { toast.error('Mikrofon nicht freigegeben'); }
  };

  const chip = (aktiv) => `px-2.5 h-8 rounded-full border text-xs ${aktiv ? 'bg-marke text-white border-marke' : 'border-border hover:bg-muted'}`;
  return (
    <>
      <Button size="sm" variant="outline" className="gap-1" onClick={() => setOffen(true)} data-testid="lead-kurzfeedback">
        <MessageSquarePlus className="w-4 h-4" /> Kurz-Feedback
      </Button>
      <Dialog open={offen} onOpenChange={(o) => { if (!o) zu(); }}>
        <DialogContent className="sm:max-w-md" data-testid="kurzfeedback-dialog">
          <DialogHeader><DialogTitle>Kurz-Feedback{name ? `: ${name}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Nachfassen am (optional)</p>
              <div className="flex flex-wrap gap-1.5">
                {vorschlaege.map(([k, l, iso]) => (
                  <button key={k} type="button" className={chip(tag === k)} onClick={() => setTag(tag === k ? '' : k)} data-testid={`kf-tag-${k}`}>{l} · {tagDe(iso)}</button>
                ))}
                <button type="button" className={chip(tag === 'eigen')} onClick={() => setTag(tag === 'eigen' ? '' : 'eigen')} data-testid="kf-tag-eigen">Datum</button>
              </div>
              {tag === 'eigen' && (
                <input type="date" min={heute} value={eigenerTag} onChange={(e) => setEigenerTag(e.target.value)} data-testid="kf-datum"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
              )}
            </div>
            <div className="flex flex-col items-center gap-1.5 py-1">
              {!nimmtAuf ? (
                <button type="button" onClick={start} disabled={sendet} data-testid="kf-mikro" aria-label="Sprechen"
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-marke text-white shadow focus:outline-none focus:ring-4 focus:ring-marke/40 disabled:opacity-50">
                  {sendet ? <Loader2 className="w-7 h-7 animate-spin" /> : <Mic className="w-7 h-7" />}
                </button>
              ) : (
                <button type="button" onClick={stopp} data-testid="kf-stopp" aria-label="Aufnahme beenden und speichern"
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive text-white shadow animate-pulse">
                  <Square className="w-6 h-6" />
                </button>
              )}
              <p className="text-xs text-muted-foreground" aria-live="polite">
                {nimmtAuf ? `Antippen zum Speichern · ${Math.floor(sek / 60)}:${String(sek % 60).padStart(2, '0')}` : sendet ? 'Wird gespeichert …' : 'Antippen und sprechen, zum Beispiel: „Bedenkzeit, ich melde mich Montag.“'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="oder tippen …" data-testid="kf-text" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={zu}>Abbrechen</Button>
                <Button size="sm" onClick={() => senden({})} disabled={sendet || nimmtAuf || text.trim().length < 3 || (tag === 'eigen' && !eigenerTag)} data-testid="kf-speichern">Speichern</Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Landet als Notiz im Verlauf. Mit Datum entsteht eine Aufgabe für dich. Die Aufnahme wird nicht gespeichert.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
